import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AlertTriangle, HelpCircle, Trash2 } from "lucide-react";

const ConfirmCtx = createContext(null);

const TONE = {
  default: { icon: HelpCircle, ring: "bg-[#284A36]/10 text-[#284A36]", action: "bg-[#284A36] hover:bg-[#1E3A28]" },
  warning: { icon: AlertTriangle, ring: "bg-[#CBA153]/10 text-[#CBA153]", action: "bg-[#CBA153] hover:bg-[#A88639]" },
  destructive: { icon: Trash2, ring: "bg-[#B85C42]/10 text-[#B85C42]", action: "bg-[#B85C42] hover:bg-[#9d4a35]" },
};

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options) => {
    setOpts(typeof options === "string" ? { description: options } : options || {});
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const finish = (value) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  };

  const tone = TONE[opts?.tone] || TONE.default;
  const Icon = tone.icon;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
        <AlertDialogContent
          className="bg-white dark:bg-[#1e1e1e] border border-[#E5E7E2] dark:border-white/10 max-w-md p-0 overflow-hidden shadow-2xl"
          data-testid="app-confirm-dialog"
        >
          <div className="px-6 pt-6 pb-4 flex gap-4">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${tone.ring}`}>
              <Icon className="h-5 w-5" />
            </div>
            <AlertDialogHeader className="text-left flex-1">
              <AlertDialogTitle className="text-base font-semibold font-heading text-[#16201A] dark:text-white">
                {opts?.title || "Are you sure?"}
              </AlertDialogTitle>
              {opts?.description && (
                <AlertDialogDescription className="text-sm text-[#647069] dark:text-white/60 whitespace-pre-line leading-relaxed">
                  {opts.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="bg-[#F7F8F6] dark:bg-white/5 border-t border-[#E5E7E2] dark:border-white/10 px-6 py-3 gap-2">
            <AlertDialogCancel
              className="mt-0 border-[#E5E7E2] dark:border-white/10 bg-white dark:bg-transparent text-[#16201A] dark:text-white hover:bg-[#F7F8F6] dark:hover:bg-white/10"
              onClick={() => finish(false)}
              data-testid="app-confirm-cancel"
            >
              {opts?.cancelLabel || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              className={`text-white ${tone.action}`}
              onClick={() => finish(true)}
              data-testid="app-confirm-ok"
            >
              {opts?.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmDialogProvider");
  return ctx;
}
